import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as cardActions from "@rentiohq/shared-frontend/dist/redux/card/card.actions";
import * as cardSelectors from "@rentiohq/shared-frontend/dist/redux/card/card.selectors";
import { ICard } from "@rentiohq/shared-frontend/dist/redux/card/card.types";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import * as externalActionActions from "@rentiohq/shared-frontend/dist/reduxV2/externalAction/externalAction.actions";
import * as externalActionSelectors from "@rentiohq/shared-frontend/dist/reduxV2/externalAction/externalAction.selectors";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { ICountQuery } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { EPropertyMemberTypes } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { RentioInternalRenderer } from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Box,
  EIconAvatarVariant,
  DataList,
  DataListItem,
  Grid,
  Icon,
  IconAvatar,
  MainCard,
  Page,
  SimpleInfoBox,
  SpinningLoader,
  Stack,
  Button,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { IFormikValues } from "@rentiohq/web-shared-next/dist/utils/types/form";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { getContractStatusFilter } from "scenes/FollowUp/EndingContractsV2/EndingContracts.utils";
import { EContractStatus } from "scenes/Properties/scenes/Contracts/components/ContractCard/components/ContractStatusChip/contractStatusChip.types";
import { StringParam } from "serialize-query-params";
import { EDeeplinkScope } from "../../../components/HeaderNotificationMenu/components/deeplink.utils";
import { CompleteContactModal } from "../../components/Modals/CompleteContact";
import { NotificationCard } from "../../components/NotificationCard";
import { PropertyCard } from "../../components/PropertyCard";

export const DashboardContainer = () => {
  const dispatch = useDispatch();
  const self = useSelf();
  const personalContactId = self?.contactMe?.id!;
  const { internalModeEnabled } = useInternalMode();

  const [clickedNotification, setClickedNotification] = React.useState<
    ICard | undefined
  >();

  const [queryParamValue, setQueryParamValue] = useQueryParams({
    documentId: StringParam,
    type: StringParam,
    contactId: StringParam,
    externalActionId: StringParam,
    externalActionKey: StringParam,
    token: StringParam,
  });

  const isPerforming = useSelector((state: IPartialRootState) =>
    externalActionSelectors.isPerformingExternalActionSelector(
      state,
      queryParamValue.type,
    ),
  );

  const error = useSelector((state: IPartialRootState) =>
    externalActionSelectors.performExternalActionError(
      state,
      queryParamValue.type,
    ),
  );

  const getFilters = ({
    isActive,
  }: {
    isArchived?: boolean;
    isActive?: boolean;
    isContractEnded?: boolean;
    isUpcoming?: boolean;
  }) => {
    const result: ICountQuery = {};

    const andFilters: { [key: string]: any }[] = [];

    if (isActive) {
      andFilters.push({ archivedAt: { is: null } });
      const runningContractStatusFilter = getContractStatusFilter(
        EContractStatus.Running,
      );

      const upcomingContractStatusFilter = getContractStatusFilter(
        EContractStatus.Upcoming,
      );

      const filters = {
        contracts: {
          or: [
            runningContractStatusFilter?.contracts,
            upcomingContractStatusFilter?.contracts,
          ],
        },
      };

      andFilters.push(filters);
    }

    result.filter = { and: andFilters };

    return result;
  };

  const notifications =
    useSelector<
      IRootStore,
      ReturnType<typeof cardSelectors.unclosedCardsSelector>
    >(state => cardSelectors.unclosedCardsSelector(state)) || [];

  const isFetchingNotifications = useSelector<IRootStore>(
    state => state.card.isFetchingCards,
  );

  const fetchNotificationsError = useSelector<IRootStore, Error | undefined>(
    state => state?.card?.fetchCardsError,
  );

  const personalContact = useSelector((state: IRootStore) =>
    contactSelectors.getContact(state, personalContactId),
  );

  const {
    items: properties = [],
    isFetching: isFetchingProperties,
    fetchError: fetchErrorProperties,
  } = propertyHooks.usePaged({
    shouldRefetch: true,
    query: {
      limit: 5,
      page: 1,
      sort: [
        {
          field: { contracts: "startDate" },
          method: "DESC",
        },
      ],
      rolesFilter: [EPropertyMemberTypes.Tenant, EPropertyMemberTypes.Parent],
      ...getFilters({ isActive: true }),
    },
  });

  React.useEffect(() => {
    document.title = `Rentio | ${getLocalizedText("dashboard.welcome")}`;
  }, []);

  const fetchCards = () => {
    dispatch(cardActions.getCards.actions.start({}));
  };

  React.useEffect(() => {
    fetchCards();
  }, []);

  React.useEffect(() => {
    if (queryParamValue.type === EDeeplinkScope.AskRemainingData) {
      dispatch(
        contactActions.getContact.actions.start({
          contactId: personalContactId,
        }),
      );
    }
  }, [queryParamValue]);

  React.useEffect(() => {
    const errorMessage =
      fetchErrorProperties || fetchNotificationsError || error;
    if (errorMessage) {
      enqueueSnackbar({
        message: errorMessage?.message,
        variant: "error",
      });
    }
  }, [fetchErrorProperties, fetchNotificationsError]);

  const handleClickCloseButton = (card?: ICard) => {
    if (!card) {
      return;
    }

    dispatch(
      cardActions.closeCard.actions.start({
        cardId: card.id,
        onSuccess: () => {
          setClickedNotification(undefined);
          fetchCards();
        },
      }),
    );
  };

  const getActionButton = (notification: ICard) => {
    const { hasCloseButton } = notification;
    if (!hasCloseButton && internalModeEnabled) {
      return (
        <RentioInternalRenderer>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => {
              if (!confirm("Force close card?")) {
                return;
              }

              handleClickCloseButton(notification);
            }}
          >
            Force close
          </Button>
        </RentioInternalRenderer>
      );
    }

    if (hasCloseButton || internalModeEnabled) {
      return (
        <Box
          onClick={() => handleClickCloseButton(notification)}
          sx={{
            cursor: "pointer",
            position: "absolute",
            right: "4px",
            top: 0,
          }}
        >
          <Icon
            path={EAssets.CloseIcon}
            width="12px"
            height="12px"
            realColor="#D84315"
          />
        </Box>
      );
    }

    return undefined;
  };

  const renderCardsList = (filteredNotifications: ICard[]) =>
    filteredNotifications.map(notification => (
      <DataListItem
        key={notification?.id}
        configuration={{
          action: getActionButton(notification),
        }}
        mainContent={
          <NotificationCard
            notification={notification}
            setClickedNotification={setClickedNotification}
            setQueryParamValue={setQueryParamValue}
          />
        }
      />
    ));

  const renderMessages = () => {
    if (isFetchingProperties || isFetchingNotifications) {
      return <SpinningLoader />;
    }

    if (notifications?.length > 0) {
      return (
        <MainCard
          title={getLocalizedText("system.messages")}
          border={true}
          startIcon={
            <IconAvatar
              icon={EAssets.AlertIcon}
              variant={EIconAvatarVariant.Warning}
            />
          }
        >
          <DataList>{renderCardsList(notifications)}</DataList>
        </MainCard>
      );
    }

    if (notifications?.length === 0) {
      return (
        <SimpleInfoBox
          headline={getLocalizedText("tenant.no_messages.heading")}
          details={getLocalizedText("tenant.no_messages.details")}
          moreInfo={getLocalizedText("tenant.no_messages.more_info")}
        />
      );
    }
  };

  const renderProperties = () => {
    if (isFetchingProperties) {
      return <SpinningLoader />;
    }

    if (properties?.length > 0) {
      return (
        <Stack gap={1}>
          {properties.map(property => (
            <PropertyCard property={property} isActive={true} />
          ))}
        </Stack>
      );
    }

    if (properties?.length === 0) {
      return (
        <SimpleInfoBox
          headline={getLocalizedText("tenant.no_rental.heading")}
          details={getLocalizedText("tenant.no_rental.details")}
          moreInfo={getLocalizedText("tenant.no_property.more_info")}
        />
      );
    }
  };

  const resetQueryParams = () => {
    setQueryParamValue({
      type: undefined,
      contactId: undefined,
      externalActionId: undefined,
      externalActionKey: undefined,
      token: undefined,
    });
  };

  const updatePersonalContact = (contact: IContact) => {
    const data = {
      // @ts-ignore
      firstname: contact.firstName,
      // @ts-ignore
      lastname: contact.lastName,
      email: contact.email,
      phone: contact.phone,
      locale: contact.locale,
      foreignIdentityNumber: contact.foreignIdentityNumber || null,
      nationalRegisterNumber: contact.nationalRegisterNumber || null,
      dateOfBirth: contact.dateOfBirth,
      nationality: contact.nationality,
      placeOfBirth: contact.placeOfBirth,
      countryOfBirth: contact.countryOfBirth,
      countryId: contact.countryId,
      street: contact.street,
      number: contact.number,
      box: contact.box,
      zip: contact.zip,
      city: contact.city,
      state: contact.state,
    };

    dispatch(
      contactActions.updateContact.actions.start({
        contactId: personalContactId,
        contact: data,
      }),
    );
  };

  const handleSubmit = (formData: IFormikValues) => {
    const { externalActionId, externalActionKey, contactId, type, token } =
      queryParamValue;

    if (externalActionId && externalActionKey && contactId && type && token) {
      const idCardFrontside = (formData as any)["IDCardFrontside"];
      const idCardBackside = (formData as any)["IDCardBackside"];

      const data: {
        updateContactDto: IContact;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        IDCardFront?: any;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        IDCardBack?: any;
      } = {
        updateContactDto: {
          // @ts-ignore
          firstName: formData.firstName,
          // @ts-ignore
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          locale: formData.locale,
          foreignIdentityNumber: formData.foreignIdentityNumber || null,
          nationalRegisterNumber: formData.nationalRegisterNumber || null,
          dateOfBirth: formData.dateOfBirth,
          nationality: formData.nationality,
          placeOfBirth: formData.placeOfBirth,
          countryOfBirth: formData.countryOfBirth,
          countryId: formData.countryId,
          street: formData.street,
          number: formData.number,
          box: formData.box,
          zip: formData.zip,
          city: formData.city,
          state: formData.state,
        },
        ...(idCardFrontside?.[0] && { IDCardFront: idCardFrontside[0] }),
        ...(idCardBackside?.[0] && { IDCardBack: idCardBackside[0] }),
      };

      dispatch(
        externalActionActions.performExternalActionStart.getAction({
          id: externalActionId,
          key: externalActionKey,
          type,
          data,
          token,
          onSuccess: () => {
            handleClickCloseButton(clickedNotification);
            updatePersonalContact(data.updateContactDto);
            resetQueryParams();
            enqueueSnackbar({
              message: "Your contact information has been updated successfully",
              variant: "success",
            });
          },
        }),
      );

      return;
    }

    // Without every one of these the request cannot be built, and silently doing nothing
    // left the tenant pressing Save with no request, no error and no feedback — which is
    // what "the website does not allow me to do it" looked like — ticket: SUP-4327.
    enqueueSnackbar({
      message: getLocalizedText("general.error"),
      variant: "error",
    });
  };

  return (
    <Page
      title={getLocalizedText("dashboard.welcome_user", {
        user: self?.user?.firstname || "",
      })}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12} md={12} lg={8} xl={9}>
          {renderMessages()}
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={4} xl={3}>
          {renderProperties()}
        </Grid>
      </Grid>
      {personalContact && (
        <CompleteContactModal
          openDialog={queryParamValue.type === EDeeplinkScope.AskRemainingData}
          isLoading={isPerforming}
          onClose={resetQueryParams}
          selectedContact={personalContact}
          onSubmit={handleSubmit}
        />
      )}
    </Page>
  );
};
